import { useState, useEffect, useRef } from 'react';
import { PenTool, Target, Hash, Calendar as CalendarIcon, X, Mic, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useTaskStore from '../../stores/useTaskStore';
import useProjectStore from '../../stores/useProjectStore';
import useAreaStore from '../../stores/useAreaStore';
import useVoiceDumpStore from '../../stores/useVoiceDumpStore';
import useInboxStore from '../../stores/useInboxStore';
import useTimerStore from '../../stores/useTimerStore';
import useFlowStore from '../../stores/useFlowStore';
import CustomDatePicker from '../ui/CustomDatePicker';
import CustomSelect from '../ui/CustomSelect';
import DualModeEditor, { getPlainText } from '../ui/DualModeEditor';
import { uploadVoiceCapture } from '../../utils/audioStorage';
import './QuickCapture.css';

/** Maximum voice recording duration in seconds (5 minutes) */
const MAX_RECORDING_DURATION_S = 300;

export default function QuickCapture() {
    const navigate = useNavigate();
    const addTask = useTaskStore(s => s.addTask);
    const projects = useProjectStore(s => s.projects);
    const areas = useAreaStore(s => s.areas);

    // Hide FABs during active focus/flow sessions
    const isTimerRunning = useTimerStore(s => s.isRunning);
    const flowExecutionState = useFlowStore(s => s.executionState);
    const isSessionActive = isTimerRunning || flowExecutionState === 'running';

    // CommandPalette uses Cmd+K. We'll use Cmd+Shift+K for Quick Capture,
    // or provide a Floating Action Button (FAB).
    const [isOpen, setIsOpen] = useState(false);
    const [content, setContent] = useState(null);
    const [projectId, setProjectId] = useState('');
    const [areaId, setAreaId] = useState('');
    const [dueDate, setDueDate] = useState('');

    // Voice capture state
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [recordingInterval, setRecordingInterval] = useState(null);

    const inputRef = useRef(null);

    const startVoiceCapture = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const chunks = [];
            const recorder = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                    ? 'audio/webm;codecs=opus'
                    : 'audio/webm'
            });
            
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.push(e.data);
            };
            
            recorder.onstop = async () => {
                const audioBlob = new Blob(chunks, { type: recorder.mimeType });
                stream.getTracks().forEach(track => track.stop());

                // Discard recording if it is empty/too small (less than 1KB, about 1s of silence)
                if (audioBlob.size < 1000) {
                    setIsRecording(false);
                    return;
                }

                setIsRecording(false);
                setIsOpen(false);

                // Upload audio to Firebase Storage and store only the URL reference.
                // This avoids storing large base64 blobs in Firestore/localStorage.
                try {
                    const reader = new FileReader();
                    const base64Data = await new Promise((resolve, reject) => {
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(audioBlob);
                    });

                    const attachmentUrl = await uploadVoiceCapture(base64Data, recorder.mimeType);
                    const addInboxItem = useInboxStore.getState().addInboxItem;
                    addInboxItem('Voice Capture', 'quick_capture', {
                        contentType: 'voice',
                        attachments: [{ type: 'audio', data: attachmentUrl, mimeType: recorder.mimeType }]
                    });
                } catch (uploadErr) {
                    console.error('Failed to upload voice capture to storage:', uploadErr);
                    // Fallback: store base64 inline if upload fails (e.g. offline)
                    const reader = new FileReader();
                    reader.readAsDataURL(audioBlob);
                    reader.onloadend = () => {
                        const addInboxItem = useInboxStore.getState().addInboxItem;
                        addInboxItem('Voice Capture', 'quick_capture', {
                            contentType: 'voice',
                            attachments: [{ type: 'audio', data: reader.result, mimeType: recorder.mimeType }]
                        });
                    };
                }
            };

            recorder.start(100);
            setMediaRecorder(recorder);
            setIsRecording(true);
            setRecordingDuration(0);
            
            const interval = setInterval(() => {
                setRecordingDuration(d => {
                    const next = d + 1;
                    // Auto-stop at 5 minute cap
                    if (next >= MAX_RECORDING_DURATION_S) {
                        recorder.stop();
                        clearInterval(interval);
                    }
                    return next;
                });
            }, 1000);
            setRecordingInterval(interval);
        } catch (err) {
            console.error("Microphone access failed:", err);
            alert("Failed to access microphone. Please check your permissions.");
        }
    };

    const stopVoiceCapture = () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }
        if (recordingInterval) {
            clearInterval(recordingInterval);
            setRecordingInterval(null);
        }
    };

    // Keyboard Shortcut listeners
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Cmd+Shift+K or Ctrl+Shift+K — always works
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setIsOpen(prev => !prev);
                return;
            }

            // "/" shortcut — only when not typing in an input/textarea/contenteditable
            if (e.key === '/' && !isOpen) {
                const tag = document.activeElement?.tagName?.toLowerCase();
                const isEditable = tag === 'input' || tag === 'textarea' || tag === 'select'
                    || document.activeElement?.isContentEditable;
                if (!isEditable) {
                    e.preventDefault();
                    setIsOpen(true);
                }
            }

            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };

        // Global event — any component can dispatch this to open Quick Capture
        const handleGlobalOpen = () => setIsOpen(true);

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('quick-capture-open', handleGlobalOpen);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('quick-capture-open', handleGlobalOpen);
        };
    }, [isOpen]);

    // Stop recording and clear intervals on close/unmount
    useEffect(() => {
        return () => {
            if (recordingInterval) {
                clearInterval(recordingInterval);
            }
            if (mediaRecorder) {
                if (mediaRecorder.state !== 'inactive') {
                    mediaRecorder.onstop = null; // Prevent saving empty/incomplete audio
                    mediaRecorder.stop();
                }
                if (mediaRecorder.stream) {
                    mediaRecorder.stream.getTracks().forEach(track => track.stop());
                }
            }
        };
    }, [recordingInterval, mediaRecorder]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current.focus(), 50);
        }
    }, [isOpen]);

    // Reset form when closed
    useEffect(() => {
        if (!isOpen) {
            if (isRecording) {
                stopVoiceCapture();
            }
            setTimeout(() => {
                setContent(null);
                setProjectId('');
                setAreaId('');
                setDueDate('');
                setRecordingDuration(0);
            }, 200);
        }
    }, [isOpen, isRecording]);

    const handleSave = (e) => {
        e?.preventDefault();
        const plainText = getPlainText(content).trim();
        if (!plainText) return;

        const isMultiLine = plainText.includes('\n');
        const defaultTitle = isMultiLine ? 'Untitled Capture' : (plainText.slice(0, 80) || 'Untitled');

        if (projectId || areaId || dueDate) {
            // It's routed somewhere specific, save as a Task
            addTask({
                title: defaultTitle,
                description: typeof content === 'object' ? getPlainText(content) : content, 
                projectId: projectId || null,
                areaId: areaId || null,
                dueDate: dueDate || null,
                priority: 'medium'
            });
        } else {
            // It's a pure brain dump, save to Inbox
            const addInboxItem = useInboxStore.getState().addInboxItem;
            addInboxItem(content, 'quick_capture');
        }

        setIsOpen(false);
    };

    const projectOptions = [
        { value: '', label: 'No Project' },
        ...projects.filter(p => p.status !== 'archived').map(p => ({
            value: p.id,
            label: `${p.emoji} ${p.name}`
        }))
    ];

    const areaOptions = [
        { value: '', label: 'No Area' },
        ...areas.map(a => ({
            value: a.id,
            label: `${a.emoji} ${a.name}`
        }))
    ];

    const openVoiceDump = useVoiceDumpStore(s => s.openWizard);

    return (
        <>
            {/* FAB group — hidden during active sessions */}
            <div className={`quick-capture-fab-group ${isOpen || isSessionActive ? 'quick-capture-fab--hidden' : ''}`}>
                <button
                    className="quick-capture-fab quick-capture-fab--mic"
                    onClick={openVoiceDump}
                    title="Voice Dump"
                >
                    <Mic size={20} />
                </button>
                <button
                    className="quick-capture-fab"
                    onClick={() => setIsOpen(true)}
                    title="Quick Capture ( / or Cmd+Shift+K)"
                >
                    <PenTool size={20} />
                </button>
            </div>

            {/* Modal Overlay */}
            {isOpen && (
                <div className="quick-capture-overlay animate-fade-in" onClick={() => setIsOpen(false)}>
                    <div className="quick-capture-modal animate-slide-up" onClick={e => e.stopPropagation()}>
                        <div className="quick-capture__header">
                            <div>
                                <h3>Quick Capture</h3>
                                <p>Capturing to <strong>{projectId || areaId || dueDate ? 'Organized Lists' : 'Inbox'}</strong></p>
                            </div>
                            <button className="btn btn-ghost btn-icon quick-capture__close" onClick={() => setIsOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="quick-capture__form">
                            <div className="quick-capture__editor-container" onKeyDown={e => {
                                // Allow Enter to submit only if in simple mode (textarea)
                                if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                                    // Make sure we're not inside a Tiptap contenteditable
                                    if (e.target.tagName.toLowerCase() !== 'div' || !e.target.isContentEditable) {
                                        e.preventDefault();
                                        if (getPlainText(content).trim()) handleSave(e);
                                    }
                                }
                            }}>
                                <DualModeEditor
                                    content={content}
                                    onUpdate={setContent}
                                    placeholder={isRecording ? "Listening to your thoughts..." : "What's on your mind?"}
                                    className="quick-capture__input"
                                    editable={!isRecording}
                                />
                            </div>

                            <div className="quick-capture__routing">
                                <div className="quick-capture__route-item">
                                    <Target size={14} className="quick-capture__route-icon" />
                                    <CustomSelect
                                        value={projectId}
                                        onChange={(val) => { setProjectId(val); setAreaId(''); }}
                                        options={projectOptions}
                                        placeholder="Add to Project"
                                        disabled={isRecording}
                                    />
                                </div>
                                <div className="quick-capture__route-item">
                                    <Hash size={14} className="quick-capture__route-icon" />
                                    <CustomSelect
                                        value={areaId}
                                        onChange={(val) => { setAreaId(val); setProjectId(''); }}
                                        options={areaOptions}
                                        placeholder="Add to Area"
                                        disabled={isRecording || !!projectId}
                                    />
                                </div>
                                <div className="quick-capture__route-item">
                                    <CalendarIcon size={14} className="quick-capture__route-icon" />
                                    <div style={{ flex: 1 }}>
                                        <CustomDatePicker
                                            value={dueDate}
                                            onChange={setDueDate}
                                            placeholder="Schedule"
                                            disabled={isRecording}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="quick-capture__footer">
                                <div className="quick-capture__hint">
                                    {isRecording ? (
                                        <span className="recording-status animate-pulse">🔴 Recording audio: {recordingDuration}s</span>
                                    ) : (
                                        <>Press <kbd>Enter</kbd> to save • <kbd>Esc</kbd> to cancel • <kbd>/</kbd> to open</>
                                    )}
                                </div>
                                <div className="quick-capture__footer-actions">
                                    <button
                                        type="button"
                                        className={`btn btn-tertiary quick-capture__mic ${isRecording ? 'quick-capture__mic--recording' : ''}`}
                                        onClick={isRecording ? stopVoiceCapture : startVoiceCapture}
                                        title={isRecording ? 'Stop Recording' : 'Record Voice Capture'}
                                    >
                                        <Mic size={16} />
                                        {isRecording ? `Stop (${recordingDuration}s)` : 'Record'}
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={!getPlainText(content).trim() || isRecording}
                                    >
                                        Capture
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
